import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import type { HubBotPalette } from '@/components/hub-bot-avatar'
import { prefersReducedMotion } from '@/lib/reduced-motion'

/**
 * The HUB bubble's motion (board zsy4, rounds 2–3): once the page is painted
 * the collapsed avatar slides in from outside the viewport edge nearest to
 * its resting spot (overshooting a little, sometimes greeting on landing),
 * and when the user leaves the view it slides back out through that edge.
 *
 * Entrances are per *view* (documents / inbox / meet), not per subject: the
 * inbox remounts the bubble for every selected email, so the decision lives
 * in `BubbleMotionProvider` (project layout) — the provider also renders the
 * exit, because the route's bubble is gone the moment navigation commits.
 */

export type EntrancePhase = 'pending' | 'off' | 'in' | 'landed' | 'done'
export type BubbleView = 'documents' | 'inbox' | 'meet'
export type Edge = 'left' | 'right' | 'top' | 'bottom'

/** Start after the first frame plus this, so the page content is already there. */
export const ENTRANCE_DELAY_MS = 300
export const ENTRANCE_MS = 800
/** The greeting (happy eyes) after landing — about every other time. */
export const LANDED_MS = 700
export const GREET_CHANCE = 0.5
export const EXIT_MS = 400
const OVERSHOOT = 'cubic-bezier(0.34, 1.56, 0.64, 1)'
const EASE_IN = 'cubic-bezier(0.4, 0, 1, 1)'

export interface Pos {
  x: number
  y: number
}
export interface Viewport {
  width: number
  height: number
}

/** The viewport edge the bubble's centre is closest to (ties go to bottom/top). */
export function nearestEdge(pos: Pos, size: number, viewport: Viewport): Edge {
  const cx = pos.x + size / 2
  const cy = pos.y + size / 2
  const distance: Record<Edge, number> = {
    bottom: viewport.height - cy,
    top: cy,
    right: viewport.width - cx,
    left: cx,
  }
  return (Object.keys(distance) as Edge[]).reduce((best, edge) =>
    distance[edge] < distance[best] ? edge : best
  )
}

/** How far to push the bubble straight out past its nearest edge. */
export function edgeOffset(pos: Pos, size: number, viewport: Viewport): { dx: number; dy: number } {
  switch (nearestEdge(pos, size, viewport)) {
    case 'left':
      return { dx: -(pos.x + size * 2), dy: 0 }
    case 'right':
      return { dx: viewport.width - pos.x + size, dy: 0 }
    case 'top':
      return { dx: 0, dy: -(pos.y + size * 2) }
    case 'bottom':
      return { dx: 0, dy: viewport.height - pos.y + size }
  }
}

const viewport = (): Viewport => ({ width: window.innerWidth, height: window.innerHeight })

export function exitStyle(pos: Pos, size: number, out: boolean): CSSProperties {
  if (!out) return { transform: 'translate(0, 0)' }
  const { dx, dy } = edgeOffset(pos, size, viewport())
  return {
    transform: `translate(${dx}px, ${dy}px)`,
    transition: `transform ${EXIT_MS}ms ${EASE_IN}`,
  }
}

/** What the provider needs to draw the exit after the bubble is gone. */
export interface BubbleSnapshot {
  pos: Pos
  size: number
  palette: HubBotPalette
  /** At rest on screen (not hidden behind the open panel, not mid-entrance). */
  visible: boolean
}

export interface BubbleMotion {
  /** A bubble instance shows `view`; true when that is new to the view (play the entrance). */
  register(view: string, instance: string): boolean
  update(view: string, instance: string, snapshot: BubbleSnapshot): void
  /** The instance is gone; unless another one registers for the view at once, the view was left. */
  unregister(view: string, instance: string): void
}

/** Outside a provider (tests, a lone bubble): every mount enters, nothing exits. */
const STANDALONE: BubbleMotion = {
  register: () => true,
  update: () => {},
  unregister: () => {},
}

export const BubbleMotionContext = createContext<BubbleMotion | null>(null)

export interface UseBubbleMotionOptions {
  view: string
  pos: Pos
  size: number
  palette: HubBotPalette
  /** The panel is open — the collapsed avatar is not on screen. */
  hidden: boolean
  /** Rolls the landing greeting; injectable for tests. */
  random?: () => number
}

export function useBubbleMotion({
  view,
  pos,
  size,
  palette,
  hidden,
  random = Math.random,
}: UseBubbleMotionOptions): { phase: EntrancePhase; style: CSSProperties; cancel: () => void } {
  const motion = useContext(BubbleMotionContext) ?? STANDALONE
  const instance = useId()
  // `pos` is unplaced until the dock effect has run.
  const placed = pos.x >= 0
  const [phase, setPhase] = useState<EntrancePhase>('pending')
  const timers = useRef<number[]>([])
  const frame = useRef(0)
  const randomRef = useRef(random)
  randomRef.current = random

  const clear = () => {
    cancelAnimationFrame(frame.current)
    for (const id of timers.current) clearTimeout(id)
    timers.current = []
  }

  // Decide before paint: a remount within the same view must not flash.
  useLayoutEffect(() => {
    if (!placed) return
    const enters = motion.register(view, instance)
    if (!enters || prefersReducedMotion()) {
      setPhase('done')
      return
    }
    setPhase('off')
    const after = (ms: number, fn: () => void) => {
      timers.current.push(window.setTimeout(fn, ms))
    }
    frame.current = requestAnimationFrame(() =>
      after(ENTRANCE_DELAY_MS, () => {
        setPhase('in')
        after(ENTRANCE_MS, () => {
          if (randomRef.current() < GREET_CHANCE) {
            setPhase('landed')
            after(LANDED_MS, () => setPhase('done'))
          } else setPhase('done')
        })
      })
    )
    return clear
  }, [motion, view, instance, placed])

  // Layout-phase cleanup: in one commit React runs it before the next
  // bubble's register, so "same view, next subject" and "left the view" are
  // told apart reliably.
  useLayoutEffect(() => () => motion.unregister(view, instance), [motion, view, instance])

  const atRest = phase === 'landed' || phase === 'done'
  useEffect(() => {
    motion.update(view, instance, { pos, size, palette, visible: placed && !hidden && atRest })
  })

  const cancel = () => {
    clear()
    setPhase('done')
  }

  let style: CSSProperties = {}
  if (!placed || phase === 'pending') style = { visibility: 'hidden' }
  else if (phase === 'off') {
    const { dx, dy } = edgeOffset(pos, size, viewport())
    style = { transform: `translate(${dx}px, ${dy}px)` }
  } else if (phase === 'in') {
    style = { transform: 'translate(0, 0)', transition: `transform ${ENTRANCE_MS}ms ${OVERSHOOT}` }
  }
  return { phase, style, cancel }
}
